/**
 * @file CierreDelDia.jsx
 * @description Los seis pasos del cierre del día. Es lo primero de la pantalla.
 *
 * QUÉ REEMPLAZA. La jornada mostraba DIEZ secciones al mismo nivel —cuadre, tesorería,
 * erosión, producción, cobranza, recaudado, contabilidad, próximos días— y ninguna decía
 * cuál era el paso siguiente. Con un día sin movimiento, ocho salían en cero y la pantalla
 * parecía rota. La queja fue literal: «no puedo estar todo un día adivinando qué paso viene».
 *
 * FILAS CONCRETAS Y SIN PROSA, como el bloque de pagos divididos — que es el único que
 * funcionó. Cada fila: el paso, en qué está, la cifra, y el botón cuando hay algo que hacer.
 *
 * NO CALCULA NADA. El estado de cada paso lo decide `cierre-dia.service` en payment: si el
 * paso siguiente se dedujera aquí habría dos definiciones del cierre, y eso ya pasó con la
 * plata del mes.
 *
 * EL PASO QUE TOCA VA MARCADO. `cierre.siguiente` nombra el primero que se PUEDE hacer, no
 * el primero pendiente: mandar a alguien a un paso bloqueado es mandarlo a mirar un botón
 * que no funciona.
 *
 * @see payment/src/services/cierre-dia.service.ts — quién decide el estado de cada paso
 */
import React from 'react';

import { Aviso, Cifra, Encabezado, Fila, Rotulo, Seccion } from './cierre/piezas';
import { Boton } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp } from '../../../lib/yoolpay/formato';

const COLS = '28px 1fr 130px 1fr 150px';

/** Qué se ve en cada estado. El tono ORDENA: sólo `listo` pide acción ahora. */
const ESTADO = {
  hecho: { marca: '✓', label: 'hecho' },
  listo: { marca: '▸', label: 'ahora' },
  bloqueado: { marca: '!', label: 'bloqueado' },
  // «Sin materia» es como se llama el estado en el código. Quien mira la pantalla no
  // tiene por qué saberlo: lo que ve es un día que no tuvo ese movimiento.
  sin_materia: { marca: '·', label: 'sin movimiento' },
};

/**
 * El verbo del botón de cada paso.
 *
 * Los cuatro pasos accionables hacen cosas distintas, y el botón es lo último que se lee
 * antes de hacer clic: «Registrar» avisa que se está anotando algo que YA pasó —la
 * transferencia salió del banco— y «Crear» que se va a escribir en Odoo. Con un «Ejecutar»
 * para todos hay que volver al título para saber cuál de las dos cosas es.
 */
/**
 * El título en el rail, donde hay 132px. «Pedidos de venta y compra» no cabe y
 * recortado con puntos suspensivos no dice nada; la tabla lo muestra completo.
 */
const TITULO_CORTO = {
  cobros: 'Ventas y cobros',
  deposito_esperado: 'Depósito esperado',
  aceptar_deposito: 'Aceptar depósito',
  contabilidad: 'Contabilidad',
  pedidos: 'Pedidos',
  giro: 'Giro a compañías',
};

const ACCION_LABEL = {
  aceptar_deposito: 'Aceptar',
  // «Exportar» es lo que hace el sistema con el archivo; lo que hace el paso es
  // contabilizar el día. Mismo cambio que se hizo en los textos de payment.
  contabilidad: 'Contabilizar',
  pedidos: 'Crear',
  giro: 'Registrar',
};

/**
 * El rail: los seis pasos de un vistazo, arriba de la tabla.
 *
 * RESPONDE OTRA PREGUNTA QUE LA TABLA. La tabla dice «¿qué hago?» —con su motivo, su
 * monto y su botón— y para eso hay que leer seis filas. El rail dice «¿cómo viene el
 * día?» sin leer ninguna: cuántos cerraron, dónde está el que toca y dónde está el
 * trancado. Es la estructura del prototipo (rail arriba, detalle abajo), y por eso se
 * suma en vez de reemplazar: quitar la tabla dejaría el paso sin su botón.
 *
 * NO ES CLICKEABLE, a propósito. La tabla está justo debajo y son seis filas: un click
 * que sólo baja seis filas es un gesto que no lleva a ninguna parte. Se rendea como
 * lista ordenada porque eso es: seis pasos en orden.
 *
 * SE DESPLAZA EN HORIZONTAL EN PANTALLA ANGOSTA en vez de apretarse. Seis nodos
 * comprimidos cortan los rótulos, y un rótulo cortado en la pantalla del cierre es
 * justo lo que no puede pasar.
 */
function RailDePasos({ pasos, siguiente }) {
  return (
    <ol
      className="cw-scroll"
      style={{
        display: 'flex',
        alignItems: 'center',
        listStyle: 'none',
        margin: 0,
        padding: '4px 24px 18px',
        gap: 0,
        overflowX: 'auto',
      }}
    >
      {pasos.map((p, i) => {
        const esElQueToca = p.clave === siguiente;
        const clase = p.estado === 'hecho'
          ? 'done'
          : esElQueToca
            ? 'here'
            : p.estado === 'bloqueado'
              ? 'attn'
              : '';
        return (
          <li
            key={p.clave}
            className={`cw-step ${clase}${i === pasos.length - 1 ? ' last' : ''}`}
            style={{ cursor: 'default', minWidth: 132 }}
            aria-current={esElQueToca ? 'step' : undefined}
          >
            <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <span className="cw-sc">{p.estado === 'hecho' ? '✓' : i + 1}</span>
              <span style={{ minWidth: 0, marginLeft: 10 }}>
                <p className="cw-sk">Paso {i + 1}</p>
                <p
                  className="cw-sl"
                  title={p.titulo}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {TITULO_CORTO[p.clave] ?? p.titulo}
                </p>
                {/* El estado en una palabra. El motivo completo vive en la tabla: acá
                    no cabe y recortarlo dejaría medio diagnóstico. */}
                <p className="cw-sn">{ESTADO[p.estado]?.label ?? p.estado}</p>
              </span>
            </span>
            <span className="cw-sline" />
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Los pasos del día.
 *
 * @param {object|null} cierre lo que `GET /api/dia` devuelve en `cierre`
 * @param {object} acciones `{ [clave]: () => void }` — el botón de cada paso que lo tenga
 * @param {object} permisos `{ registrar_giro: {puede, motivo} }` — de `GET /api/dia`
 */
export default function CierreDelDia({ cierre, acciones = {}, permisos = null }) {
  // `null` NO es un día sin pasos: es que no se pudo saber. Decirlo es distinto de mostrar
  // seis filas vacías, que se leerían como «no falta nada».
  if (!cierre) {
    return (
      <Seccion titulo="Cierre del día">
        <div style={{ padding: '18px 24px' }}>
          <Aviso tono="media" titulo="No se pudo calcular el cierre del día.">
            Falta la tesorería del día, así que no se puede decir en qué paso está. El resto
            de la pantalla sigue mostrando lo que sí se pudo leer.
          </Aviso>
        </div>
      </Seccion>
    );
  }

  const sinMateria = cierre.pasos.every((p) => p.estado === 'sin_materia');

  return (
    <Seccion
      titulo="Cierre del día"
      bajada={
        sinMateria
          ? 'Este día no tuvo cobros, así que no hay nada que cerrar.'
          : cierre.liquidado
            ? 'El día está liquidado: los seis pasos están hechos.'
            : 'Los seis pasos, en orden. El marcado con ▸ es el que toca ahora.'
      }
      derecha={
        cierre.liquidado ? (
          <span style={{ fontSize: 12.5, fontWeight: 600, color: '#007D6E' }}>
            Día liquidado
          </span>
        ) : null
      }
    >
      {sinMateria ? (
        <div style={{ padding: '18px 24px' }}>
          <Aviso tono="baja" titulo="Sin cobros este día.">
            No hubo movimiento, así que no hay depósito que aceptar ni giro que registrar.
          </Aviso>
        </div>
      ) : (
        <>
          <RailDePasos pasos={cierre.pasos} siguiente={cierre.siguiente} />
          <Encabezado cols={COLS}>
            <Rotulo> </Rotulo>
            <Rotulo>Paso</Rotulo>
            <Rotulo align="right">Monto</Rotulo>
            <Rotulo>En qué está</Rotulo>
            <Rotulo align="right">Acción</Rotulo>
          </Encabezado>

          {cierre.pasos.map((p, i) => {
            const e = ESTADO[p.estado] ?? ESTADO.sin_materia;
            const esElQueToca = p.clave === cierre.siguiente;
            const accion = acciones[p.clave];
            // QUÉ PUEDE ESTA PERSONA, que no es lo mismo que en qué estado está el paso.
            // El paso 6 puede estar «listo» —el mundo permite girar— y aun así esta
            // persona no poder, porque fue ella quien aceptó el depósito (R22).
            const permiso = p.clave === 'giro' ? permisos?.registrar_giro : null;
            // DESHABILITADO SÓLO CUANDO DE VERDAD NO SE PUEDE. Hoy eso pasa únicamente si
            // no hay autor verificado. El caso «tú aceptaste el depósito» ya NO bloquea:
            // pide una razón escrita al hacer clic, así que el botón queda activo y el
            // aviso se muestra igual — esconderlo sería quitar la advertencia junto con
            // el bloqueo.
            const vetado = permiso != null && permiso.puede === false;
            const pideRazon = permiso?.requiere_justificacion === true;
            return (
              <Fila key={p.clave} cols={COLS} destacada={esElQueToca}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: p.estado === 'hecho'
                      ? '#007D6E'
                      : p.estado === 'bloqueado'
                        ? '#B42318'
                        : 'var(--yool-muted)',
                  }}
                >
                  {e.marca}
                </span>

                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: esElQueToca ? 700 : 600 }}>
                    {i + 1}. {p.titulo}
                  </span>
                  {/*
                    EL BLOQUEO VA PEGADO AL PASO, no en un aviso aparte. Un paso que dice
                    «bloqueado» sin decir por qué obliga a ir a buscar la razón a otro lado.
                  */}
                  {p.bloqueo ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: '#B42318',
                        marginTop: 2,
                      }}
                    >
                      {p.bloqueo}
                    </span>
                  ) : null}
                  {/*
                    EL MOTIVO VA VISIBLE, no en un `title`. Un tooltip aparece al pasar el
                    mouse y no existe en touch: dejar ahí la única explicación es esconderla
                    de nuevo, que es justo lo que R24 prohíbe. El `title` queda igual, para
                    el motivo completo si el texto se corta.
                  */}
                  {vetado || pideRazon ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: CIFRA.ambar,
                        marginTop: 2,
                      }}
                    >
                      {permiso.motivo}
                    </span>
                  ) : null}
                </span>

                {/* `null` se pinta «—», no «$0»: un cero diría que la cifra es cero. */}
                <Cifra size={12.5}>{p.monto_clp == null ? '—' : fmtClp(p.monto_clp)}</Cifra>

                <span style={{ fontSize: 12, color: 'var(--yool-gray1)' }}>
                  {p.detalle}
                  {/*
                    DE DÓNDE SALIÓ LA CIFRA. El depósito no se teclea: el sistema lee la
                    cartola de Odoo e identifica la línea. Decir de qué cartola viene es lo
                    que permite ir a verificarla — una cifra sin origen hay que creerla.
                  */}
                  {p.sugerido?.cartola ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: 'var(--yool-muted)',
                        marginTop: 2,
                      }}
                    >
                      Leído de {p.sugerido.cartola}
                    </span>
                  ) : null}
                </span>

                <span style={{ textAlign: 'right' }}>
                  {/*
                    EL BOTÓN SÓLO EN EL PASO QUE SE PUEDE HACER. En uno bloqueado sería un
                    botón que no funciona, y en uno hecho invitaría a repetir algo hecho.
                  */}
                  {accion && p.estado === 'listo' ? (
                    /*
                      DESHABILITADO CON EXPLICACIÓN, NO ESCONDIDO (R24). Y es la excepción
                      justa a la regla de arriba: un paso BLOQUEADO no lleva botón porque
                      nadie puede hacerlo todavía —el mundo no está listo—; éste está listo
                      para cualquiera menos para quien mira. Esconderlo ahí haría creer que
                      el sistema está roto, que es literalmente lo que la regla dice evitar.
                      Dejarlo activo para fallar con un 403 es peor: manda a intentar algo
                      que ya se sabía que no iba a poder.
                    */
                    <Boton
                      variant={vetado ? 'outline' : esElQueToca ? 'primary' : 'outline'}
                      onClick={accion}
                      disabled={vetado}
                      title={vetado ? permiso.motivo : undefined}
                    >
                      {/*
                        EL VERBO DE CADA PASO, no un «Ejecutar» para los cuatro. Hacen cosas
                        distintas —aceptar contra la cartola, mandar asientos a Odoo, crear
                        pedidos, registrar una transferencia que ya salió— y con la misma
                        palabra hay que leer el título para saber qué va a pasar.
                      */}
                      {ACCION_LABEL[p.clave] ?? 'Ejecutar'}
                    </Boton>
                  ) : (
                    <span style={{ fontSize: 11.5, color: 'var(--yool-muted)' }}>
                      {e.label}
                    </span>
                  )}
                </span>
              </Fila>
            );
          })}
        </>
      )}
    </Seccion>
  );
}
